import { useEffect, useState, useCallback } from "react";
import startCase from "lodash/startCase";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import { toggleAdminSetting, setAdminSettingValue } from "sj-ui-toolkit/src/api/admin/settings";
import {
  fetchSiteSettings,
  updateAdminInfo,
  uploadLogoImage,
  clearLogoImage,
} from "@/api/settings";
import { getAdminSettings } from "sj-ui-toolkit/src/api/admin/common";
import type { AdminSettings } from "@/api/settings";

import { CustomizationSection } from "@/components/settings/CustomizationSection";
import { LevelSettings } from "@/components/settings/LevelSettings";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SettingsState extends Record<string, unknown> {
  numberOfTries: number | null | "loading";
}

interface SiteSettingsState extends Record<string, unknown> {
  achievementWall: boolean | "loading";
  allowUserProfileEdits: boolean | "loading";
  anonymousLeaderboard: boolean | "loading";
  catalog: boolean | "loading";
  contacts: string | "loading";
  disclaimerText: string | "loading";
  dojoPreviewVideoId: string | "loading";
  journeyPreviewVideoId: number | "loading";
  leaderboard: boolean | "loading";
  participationEditing: boolean | "loading";
  rankingQuestions: boolean | "loading";
  samlApprove: boolean | "loading";
  sjPathsActivated: boolean | "loading";
  sso: boolean | "loading";
  strictAssignment: boolean | "loading";
  enrollmentCompletionNotificationEndpoint: string | "loading";
  thumbLogoUrl: string | null | "loading";
  darkLogoBackground: boolean | "loading";
  levelViewType: string | "loading";
}

const textFields = ["contacts", "disclaimerText"];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Settings = () => {
  useBreadcrumb({
    label: "Settings",
    route: AdminAppRoutes.routes.settings.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const { refetchSettings } = useSettingsContext();

  const [settings, setSettings] = useState<SettingsState>({
    numberOfTries: "loading",
  });

  const [siteSettings, setSiteSettings] = useState<SiteSettingsState>({
    achievementWall: "loading",
    allowUserProfileEdits: "loading",
    anonymousLeaderboard: "loading",
    catalog: "loading",
    contacts: "loading",
    disclaimerText: "loading",
    dojoPreviewVideoId: "loading",
    journeyPreviewVideoId: "loading",
    leaderboard: "loading",
    participationEditing: "loading",
    rankingQuestions: "loading",
    samlApprove: "loading",
    sjPathsActivated: "loading",
    sso: "loading",
    strictAssignment: "loading",
    enrollmentCompletionNotificationEndpoint: "loading",
    thumbLogoUrl: "loading",
    darkLogoBackground: "loading",
    levelViewType: "loading",
  });

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      showToast({ severity, summary, detail, life });
    },
    [showToast]
  );

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = (await getAdminSettings()) as AdminSettings;
        const rawTries = settingsData.numberOfTries;
        const parsedTries = Number(rawTries);
        setSettings(prev => ({
          ...prev,
          numberOfTries: rawTries != null && Number.isFinite(parsedTries) ? parsedTries : null,
        }));
      } catch (error) {
        console.error("Failed to load admin settings", error);
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load admin settings",
        });
      }
    };

    loadSettings();
  }, [showToast]);

  useEffect(() => {
    const fetchSiteSettingsData = async () => {
      try {
        const siteData = await fetchSiteSettings();
        if (!siteData) return;

        setSiteSettings(prev => ({
          ...prev,
          achievementWall: siteData.achievementWall,
          allowUserProfileEdits: siteData.allowUserProfileEdits,
          anonymousLeaderboard: siteData.anonymousLeaderboard,
          catalog: siteData.catalog,
          contacts: siteData.contacts,
          disclaimerText: siteData.disclaimerText,
          dojoPreviewVideoId: siteData.dojoPreviewVideoId,
          journeyPreviewVideoId: siteData.journeyPreviewVideoId,
          leaderboard: siteData.leaderboard,
          participationEditing: siteData.participationEditing,
          rankingQuestions: siteData.rankingQuestions,
          samlApprove: siteData.samlApprove,
          sjPathsActivated: siteData.sjPathsActivated,
          sso: siteData.sso,
          strictAssignment: siteData.strictAssignment,
          levelViewType: siteData.levelViewType,
          enrollmentCompletionNotificationEndpoint:
            siteData.enrollmentCompletionNotificationEndpoint,
          thumbLogoUrl: siteData.thumbLogoUrl,
          darkLogoBackground: siteData.darkLogoBackground,
        }));
      } catch (error) {
        console.error("Error fetching site settings:", error);
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load site settings",
        });
      }
    };

    fetchSiteSettingsData();
  }, [showToast]);

  const updateSiteSettings = async (field: string, value: unknown) => {
    setSiteSettings(prev => ({
      ...prev,
      [field]: value,
    }));

    if (!textFields.includes(field)) {
      try {
        await toggleAdminSetting(field, value as boolean);
        showLocalToast("success", "Success", `${startCase(field)} setting updated successfully`);
      } catch (error) {
        console.error(`Error updating ${field}:`, error);
        showLocalToast(
          "error",
          "Error",
          `Failed to update ${startCase(field)} setting. Please try again.`
        );
      }
    }
  };

  const updateAdminSettings = async (field: string, value: unknown) => {
    // Don't persist an empty/cleared number field — avoids writing the literal
    // string "null" (and the NaN render that follows) for an unset value.
    if (value == null || (typeof value === "number" && Number.isNaN(value))) {
      return;
    }

    const previousValue = settings[field as keyof SettingsState];

    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));

    try {
      await setAdminSettingValue(field, String(value));
      showLocalToast("success", "Success", `${startCase(field)} setting updated successfully`);
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setSettings(prev => ({
        ...prev,
        [field]: previousValue,
      }));
      showLocalToast(
        "error",
        "Error",
        `Failed to update ${startCase(field)} setting. Please try again.`
      );
    }
  };

  const updateSettingsTextField = async (field: string) => {
    const value = siteSettings[field as keyof SiteSettingsState];
    if (value === "loading") return;

    try {
      await updateAdminInfo({ [field]: value as string });
      showLocalToast("success", "Success", `${startCase(field)} setting updated successfully`);
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      showLocalToast(
        "error",
        "Error",
        `Failed to update ${startCase(field)} setting. Please try again.`
      );
    }
  };

  const updateTheme = async (theme: string) => {
    if (theme !== siteSettings.levelViewType) {
      setSiteSettings(prev => ({
        ...prev,
        levelViewType: theme,
      }));
      try {
        await setAdminSettingValue("level_view_type", theme);
        showLocalToast("success", "Success", "Theme updated successfully");
      } catch (error) {
        console.error("Error updating theme:", error);
        showLocalToast("error", "Error", "Failed to update theme. Please try again.");
      }
    }
  };

  const uploadLogo = async (file: File) => {
    try {
      const response = await uploadLogoImage(file);
      if (response) {
        const { thumbLogoUrl } = response;
        setSiteSettings(prev => ({
          ...prev,
          ...(thumbLogoUrl && { thumbLogoUrl }),
        }));
        await refetchSettings();
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      throw error;
    }
  };

  const removeLogo = async () => {
    try {
      await clearLogoImage();
      setSiteSettings(prev => ({ ...prev, thumbLogoUrl: null }));
      await refetchSettings();
    } catch (error) {
      console.error("Error removing logo:", error);
      throw error;
    }
  };

  return (
    <>
      <div className="mb-4">
        <h1>Settings</h1>
      </div>
      <div className="flex flex-col gap-4 mb-8">
        <CustomizationSection
          settings={settings as Record<string, unknown>}
          siteSettings={siteSettings as Record<string, unknown>}
          updateSettings={updateAdminSettings}
          updateSiteSettings={updateSiteSettings}
          updateSettingsTextField={updateSettingsTextField}
          uploadLogo={uploadLogo}
          removeLogo={removeLogo}
          showToast={showLocalToast}
        />

        <LevelSettings
          currentTheme={
            siteSettings.levelViewType === "loading" ? undefined : siteSettings.levelViewType
          }
          updateTheme={updateTheme}
          showToast={showLocalToast}
        />
      </div>
    </>
  );
};

export default Settings;
